import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const StatusContext = createContext();

export function StatusProvider({ children }) {
  const LS_KEY = 'hm_page_statuses_v1';
  const [pageStatuses, setPageStatuses] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch {}
    return {
      nodes: 'grey',
      fastUpdates: 'grey',
      ftsoSubmissions: 'grey',
      ftsoOverview: 'grey',
      fdcSubmissions: 'grey',
      registration: 'grey',
      validators: 'grey',
      balances: 'grey',
      indexers: 'grey'
    };
  });

  // Hysteresis to prevent flicker on transient downgrades during data refreshes
  // We buffer downgrades for a short window and suppress 'grey' once a page has a known status
  const DOWNGRADE_HYSTERESIS_MS = 3000; // 3s stabilization window
  const stabilityRef = useRef({}); // page -> { pendingTarget?: string, sinceTs?: number, seenNonGrey?: boolean }

  // Hydrate stability flags for persisted non-grey statuses to avoid initial grey flicker
  useEffect(() => {
    try {
      Object.entries(pageStatuses || {}).forEach(([page, status]) => {
        const st = stabilityRef.current[page] || { pendingTarget: undefined, sinceTs: 0, seenNonGrey: false };
        if ((status || 'grey') !== 'grey' && !st.seenNonGrey) {
          stabilityRef.current[page] = { ...st, seenNonGrey: true };
        }
      });
    } catch {}
    // Persist immediately on first mount as well
    try { localStorage.setItem(LS_KEY, JSON.stringify(pageStatuses)); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist status changes for instant RAG on hard refresh
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(pageStatuses));
    } catch {}
  }, [pageStatuses]);

  const rank = (s) => {
    switch ((s || 'grey').toLowerCase()) {
      case 'green': return 3;
      case 'amber':
      case 'yellow': return 2;
      case 'red': return 1;
      case 'grey':
      default: return 0;
    }
  };

  // Enhanced updatePageStatus with logging
  const updatePageStatus = useCallback((page, status) => {
    setPageStatuses(prev => {
      const prevStatus = prev[page] || 'grey';
      const nextStatus = (status || 'grey').toLowerCase();

      // Initialize stability state for page
      const st = stabilityRef.current[page] || { pendingTarget: undefined, sinceTs: 0, seenNonGrey: false };

      // Never regress to grey after we've seen a non-grey status in this session
      if (nextStatus === 'grey' && (st.seenNonGrey || rank(prevStatus) > 0)) {
        stabilityRef.current[page] = { ...st, seenNonGrey: true, pendingTarget: undefined, sinceTs: 0 };
        return prev;
      }

      const prevRank = rank(prevStatus);
      const nextRank = rank(nextStatus);

      // Upgrades apply immediately; clear any pending downgrade
      if (nextRank > prevRank) {
        stabilityRef.current[page] = { pendingTarget: undefined, sinceTs: 0, seenNonGrey: nextStatus !== 'grey' || st.seenNonGrey };
        if (prevStatus !== nextStatus) {
          return { ...prev, [page]: nextStatus };
        }
        return prev;
      }

      // Equal -> no change; but record that we have a non-grey seen
      if (nextRank === prevRank) {
        if (nextStatus !== 'grey' && !st.seenNonGrey) {
          stabilityRef.current[page] = { ...st, seenNonGrey: true };
        }
        return prev;
      }

      // Downgrades: buffer within hysteresis window; commit only if persistent
      const now = Date.now();
      if (st.pendingTarget !== nextStatus) {
        // Start/replace downgrade pending window
        stabilityRef.current[page] = { pendingTarget: nextStatus, sinceTs: now, seenNonGrey: st.seenNonGrey || prevStatus !== 'grey' };
        return prev;
      }
      const elapsed = now - (st.sinceTs || 0);
      if (elapsed < DOWNGRADE_HYSTERESIS_MS) {
        // Still within buffer window -> keep previous status
        return prev;
      }
      // Downgrade persisted long enough -> commit
      stabilityRef.current[page] = { pendingTarget: undefined, sinceTs: 0, seenNonGrey: st.seenNonGrey || prevStatus !== 'grey' };
      if (prevStatus !== nextStatus) {
        return { ...prev, [page]: nextStatus };
      }
      return prev;
    });
  }, []);

  return (
    <StatusContext.Provider value={{ pageStatuses, updatePageStatus }}>
      {children}
    </StatusContext.Provider>
  );
}

export const useStatus = () => useContext(StatusContext);